import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface Installer {
  id: string;
  company: string;
  contact: string;
  email: string;
  phone: string;
  service_states: string[];
  contract_signed: boolean;
  signed_at: string | null;
  role: string;
  created_at: string;
  total_purchases: number;
  total_spent: number;
}

type FilterStatus = "all" | "signed" | "pending";
type SortKey = "created_at" | "company" | "total_purchases" | "total_spent";

function StatPill({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-5 py-4 flex flex-col gap-1">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-400">{label}</p>
    </div>
  );
}

function DrawerRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-50 last:border-0 gap-4">
      <span className="text-xs text-gray-400 font-medium whitespace-nowrap">{label}</span>
      <span className="text-sm text-gray-800 font-medium text-right">{value}</span>
    </div>
  );
}

interface PurchaseRow {
  id: string;
  amount: number;
  status: string;
  created_at: string;
  leads: { city: string; state: string; name: string } | null;
}

function InstallerDrawer({
  installer,
  onClose,
  onContractToggle,
}: {
  installer: Installer;
  onClose: () => void;
  onContractToggle: (id: string, current: boolean) => void;
}) {
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingPurchases(true);
      const { data } = await supabase
        .from("lead_purchases")
        .select("id, amount, status, created_at, leads(city, state, name)")
        .eq("installer_id", installer.id)
        .order("created_at", { ascending: false })
        .limit(20);
      setPurchases((data as unknown as PurchaseRow[]) || []);
      setLoadingPurchases(false);
    };
    load();
  }, [installer.id]);

  const handleToggle = async () => {
    setToggling(true);
    await onContractToggle(installer.id, installer.contract_signed);
    setToggling(false);
  };

  const initials = installer.company
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-bold">{initials}</span>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">{installer.company}</p>
              <p className="text-xs text-gray-400">{installer.contact}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition cursor-pointer text-gray-400"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3 p-5 bg-gray-50 border-b border-gray-100">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-900">{installer.total_purchases}</p>
              <p className="text-xs text-gray-400 mt-0.5">Leads Bought</p>
            </div>
            <div className="text-center border-x border-gray-200">
              <p className="text-xl font-bold text-[#16a34a]">${installer.total_spent.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-0.5">Total Spent</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-900">
                {installer.service_states?.length || 0}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">States</p>
            </div>
          </div>

          {/* Details */}
          <div className="px-6 py-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Account Details</p>
            <DrawerRow label="Email" value={<a href={`mailto:${installer.email}`} className="text-[#16a34a] hover:underline">{installer.email}</a>} />
            <DrawerRow label="Phone" value={installer.phone || "—"} />
            <DrawerRow label="Role" value={<span className="capitalize">{installer.role || "installer"}</span>} />
            <DrawerRow
              label="Contract"
              value={
                installer.contract_signed ? (
                  <span className="flex items-center gap-1 text-[#16a34a]">
                    <i className="ri-checkbox-circle-fill"></i>
                    Signed {installer.signed_at ? `· ${new Date(installer.signed_at).toLocaleDateString()}` : ""}
                  </span>
                ) : (
                  <span className="text-amber-600 flex items-center gap-1">
                    <i className="ri-time-line"></i> Pending
                  </span>
                )
              }
            />
            <DrawerRow
              label="Service States"
              value={
                installer.service_states?.length > 0 ? (
                  <div className="flex flex-wrap gap-1 justify-end">
                    {installer.service_states.map((s) => (
                      <span key={s} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                  </div>
                ) : "—"
              }
            />
            <DrawerRow label="Joined" value={new Date(installer.created_at).toLocaleDateString("en-US", { dateStyle: "medium" })} />
          </div>

          {/* Purchase history */}
          <div className="px-6 pb-6">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Purchase History</p>
            {loadingPurchases ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : purchases.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">
                <i className="ri-shopping-bag-line text-2xl block mb-1"></i>
                No purchases yet
              </div>
            ) : (
              <div className="space-y-2">
                {purchases.map((p) => (
                  <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {p.leads ? `${p.leads.name || "Lead"} · ${p.leads.city}, ${p.leads.state}` : "Lead"}
                      </p>
                      <p className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          p.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : p.status === "refunded"
                            ? "bg-red-100 text-red-500"
                            : "bg-amber-100 text-amber-600"
                        }`}
                      >
                        {p.status}
                      </span>
                      <span className="font-bold text-gray-900 text-sm">${p.amount}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={handleToggle}
            disabled={toggling}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2 ${
              installer.contract_signed
                ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                : "bg-[#16a34a] text-white hover:bg-[#15803d]"
            }`}
          >
            {toggling ? (
              <><i className="ri-loader-4-line animate-spin"></i> Updating...</>
            ) : installer.contract_signed ? (
              <><i className="ri-close-circle-line"></i> Revoke Contract</>
            ) : (
              <><i className="ri-checkbox-circle-line"></i> Mark Contract Signed</>
            )}
          </button>
          <a
            href={`mailto:${installer.email}`}
            className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 transition cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-mail-line"></i> Email
          </a>
        </div>
      </div>
    </>
  );
}

export default function InstallerTable() {
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState<Installer | null>(null);

  const fetchInstallers = useCallback(async () => {
    setLoading(true);
    const { data: rows } = await supabase
      .from("installers")
      .select("id, company, contact, email, phone, service_states, contract_signed, signed_at, role, created_at")
      .order("created_at", { ascending: false });

    if (!rows) { setLoading(false); return; }

    // Fetch purchase aggregates
    const { data: purchaseAgg } = await supabase
      .from("lead_purchases")
      .select("installer_id, amount, status")
      .eq("status", "completed");

    const aggMap: Record<string, { count: number; total: number }> = {};
    (purchaseAgg || []).forEach((p) => {
      if (!aggMap[p.installer_id]) aggMap[p.installer_id] = { count: 0, total: 0 };
      aggMap[p.installer_id].count += 1;
      aggMap[p.installer_id].total += Number(p.amount);
    });

    const enriched: Installer[] = rows.map((r) => ({
      ...(r as Omit<Installer, "total_purchases" | "total_spent">),
      total_purchases: aggMap[r.id]?.count || 0,
      total_spent: aggMap[r.id]?.total || 0,
    }));

    setInstallers(enriched);
    setLoading(false);
  }, []);

  useEffect(() => { fetchInstallers(); }, [fetchInstallers]);

  const handleContractToggle = async (id: string, current: boolean) => {
    const update = current
      ? { contract_signed: false, signed_at: null }
      : { contract_signed: true, signed_at: new Date().toISOString() };
    await supabase.from("installers").update(update).eq("id", id);
    setInstallers((prev) =>
      prev.map((i) => (i.id === id ? { ...i, ...update } : i))
    );
    if (selected?.id === id) {
      setSelected((prev) => prev ? { ...prev, ...update } : prev);
    }
  };

  // Derived stats
  const totalInstallers = installers.length;
  const signedCount = installers.filter((i) => i.contract_signed).length;
  const pendingCount = totalInstallers - signedCount;
  const totalRevenue = installers.reduce((s, i) => s + i.total_spent, 0);

  // Filter + search + sort
  const filtered = installers
    .filter((i) => {
      if (filterStatus === "signed") return i.contract_signed;
      if (filterStatus === "pending") return !i.contract_signed;
      return true;
    })
    .filter((i) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        i.company.toLowerCase().includes(q) ||
        i.contact.toLowerCase().includes(q) ||
        i.email.toLowerCase().includes(q) ||
        (i.service_states || []).some((s) => s.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      let av: string | number = a[sortKey] as string | number;
      let bv: string | number = b[sortKey] as string | number;
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else { setSortKey(key); setSortAsc(false); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <i className="ri-arrow-up-down-line ml-0.5 text-gray-300"></i>;
    return sortAsc
      ? <i className="ri-arrow-up-s-line ml-0.5 text-[#16a34a]"></i>
      : <i className="ri-arrow-down-s-line ml-0.5 text-[#16a34a]"></i>;
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Installers</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage all registered installer accounts</p>
        </div>
        <button
          onClick={fetchInstallers}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-medium transition cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i> Refresh
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatPill label="Total Installers" value={totalInstallers} color="text-gray-900" />
        <StatPill label="Contract Signed" value={signedCount} color="text-[#16a34a]" />
        <StatPill label="Pending Contract" value={pendingCount} color="text-amber-600" />
        <StatPill label="Total Revenue" value={`$${totalRevenue.toLocaleString()}`} color="text-[#16a34a]" />
      </div>

      {/* Filters + search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, contact, email or state..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {(["all", "signed", "pending"] as FilterStatus[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilterStatus(f)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition cursor-pointer whitespace-nowrap capitalize ${
                filterStatus === f ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {f === "all" ? `All (${totalInstallers})` : f === "signed" ? `Signed (${signedCount})` : `Pending (${pendingCount})`}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th
                  className="text-left text-xs text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-gray-600 whitespace-nowrap select-none"
                  onClick={() => toggleSort("company")}
                >
                  Company <SortIcon k="company" />
                </th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 whitespace-nowrap">Contact</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 whitespace-nowrap">Service States</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 whitespace-nowrap">Contract</th>
                <th
                  className="text-left text-xs text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-gray-600 whitespace-nowrap select-none"
                  onClick={() => toggleSort("total_purchases")}
                >
                  Leads Bought <SortIcon k="total_purchases" />
                </th>
                <th
                  className="text-left text-xs text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-gray-600 whitespace-nowrap select-none"
                  onClick={() => toggleSort("total_spent")}
                >
                  Total Spent <SortIcon k="total_spent" />
                </th>
                <th
                  className="text-left text-xs text-gray-400 font-medium px-4 py-3 cursor-pointer hover:text-gray-600 whitespace-nowrap select-none"
                  onClick={() => toggleSort("created_at")}
                >
                  Joined <SortIcon k="created_at" />
                </th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(8)].map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <i className="ri-team-line text-3xl"></i>
                      <p className="text-sm font-medium">
                        {search ? "No installers match your search" : "No installers yet"}
                      </p>
                      {search && (
                        <button onClick={() => setSearch("")} className="text-xs text-[#16a34a] hover:underline cursor-pointer">
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((inst) => {
                  const initials = inst.company
                    .split(" ")
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase();

                  return (
                    <tr
                      key={inst.id}
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => setSelected(inst)}
                    >
                      {/* Company */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">{initials}</span>
                          </div>
                          <span className="font-semibold text-gray-900 whitespace-nowrap">{inst.company}</span>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-3">
                        <p className="text-gray-700 whitespace-nowrap">{inst.contact}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[160px]">{inst.email}</p>
                      </td>

                      {/* States */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[180px]">
                          {(inst.service_states || []).slice(0, 3).map((s) => (
                            <span key={s} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">{s}</span>
                          ))}
                          {(inst.service_states || []).length > 3 && (
                            <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">
                              +{inst.service_states.length - 3}
                            </span>
                          )}
                          {(inst.service_states || []).length === 0 && <span className="text-gray-300">—</span>}
                        </div>
                      </td>

                      {/* Contract */}
                      <td className="px-4 py-3">
                        {inst.contract_signed ? (
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                            <i className="ri-checkbox-circle-fill"></i> Signed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                            <i className="ri-time-line"></i> Pending
                          </span>
                        )}
                      </td>

                      {/* Leads bought */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">{inst.total_purchases}</span>
                          {inst.total_purchases > 0 && (
                            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#16a34a] rounded-full"
                                style={{ width: `${Math.min(100, (inst.total_purchases / 10) * 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Total spent */}
                      <td className="px-4 py-3">
                        <span className={`font-bold ${inst.total_spent > 0 ? "text-[#16a34a]" : "text-gray-300"}`}>
                          ${inst.total_spent.toLocaleString()}
                        </span>
                      </td>

                      {/* Joined */}
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {new Date(inst.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelected(inst); }}
                            className="text-xs text-gray-500 hover:text-gray-900 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition cursor-pointer whitespace-nowrap"
                          >
                            View
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleContractToggle(inst.id, inst.contract_signed); }}
                            className={`text-xs font-medium px-3 py-1.5 rounded-lg transition cursor-pointer whitespace-nowrap ${
                              inst.contract_signed
                                ? "text-red-500 hover:bg-red-50 border border-red-200"
                                : "text-[#16a34a] hover:bg-green-50 border border-green-200"
                            }`}
                          >
                            {inst.contract_signed ? "Revoke" : "Approve"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer count */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-50 text-xs text-gray-400">
            Showing {filtered.length} of {totalInstallers} installer{totalInstallers !== 1 ? "s" : ""}
            {search && ` matching "${search}"`}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <InstallerDrawer
          installer={selected}
          onClose={() => setSelected(null)}
          onContractToggle={handleContractToggle}
        />
      )}
    </div>
  );
}
